import { resources } from "@constants/hardcoded-strings";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import differenceInDays from "date-fns/differenceInDays";
import Button from "react-bootstrap/Button";
import { VERIFICATION_SERVICES_URL, REPORTS_TRANSACTIONS_URL } from "@constants/routes";
import { DASHBOARD_REFERENCE_DATA_TYPES, DashboardReferenceData } from "@constants/reference-data";
import { useReferenceDataSelector } from "@hooks/use-reference-data-selector";
import { TimeRangeTypes } from "@enums/time-range-types";
import { DashboardGroupByTypes } from "@enums/dashboard-group-by-types";
import { ReferenceDataSourceTypes } from "@enums/reference-data-source-types";
import { CommonReferenceDataPageTypes } from "@enums/common-reference-data-page-types";
import { DashboardSearchFiltersState } from "@idw-types/state/dashboard-state";
import { setDashboardSearchFilters } from "@redux/actions/dashboard-actions";
import { getDashboardDataReduxRequest } from "@redux-requests/dashboard";
import { getCommonReferenceDataReduxRequest } from "@redux-requests/reference-data/get-common-reference-data-redux-request";
import { isAxnAdminUser } from "@utils/permissions-util";
import { navigateToUtil, navigateToUtilWithQueryParameters } from "@utils/navigation-util";
import { CardContainer } from "../common/card-container/card-container";
import { ContentPageBody } from "../common/content-page-body/content-page-body";
import { LoadingIndicator } from "../common/loading-indicator/loading-indicator";
import { ContentPageHeader } from "../common/content-page-header/content-page-header";
import { DashboardSearchForm } from "./dashboard-search-form/dashboard-search-form";
import { DashboardSummaryContainer } from "./dashboard-summary-container/dashboard-summary-container";
import { DashboardVerificationTransactionsTableContainer } from "./dashboard-verification-transactions-table-container/dashboard-verification-transactions-table-container";
import { DashboardVerificationServicesTableContainer } from "./dashboard-verification-services-table-container/dashboard-verification-services-table-container";
import { DashboardVerificationCountsContainer } from "./dashboard-verification-counts-container/dashboard-verification-counts-container";
import { getDashboardState, getSessionState } from "@redux/selectors/root-selectors";

export const DashboardContainer = () => {
    const navigate = useNavigate();
    const routerLocation = useLocation();

    const dispatch = useDispatch();
    const { session } = useSelector(getSessionState);
    const { searchFilters, searchResults } = useSelector(getDashboardState);
    const referenceData = useReferenceDataSelector<DashboardReferenceData>(DASHBOARD_REFERENCE_DATA_TYPES);

    const [shouldLoadReferenceData, setShouldLoadReferenceData] = useState(true);
    const [shouldLoadDashboardData, setShouldLoadDashboardData] = useState(true);

    useEffect(() => {
        if (!referenceData.isLoading.dashboard && shouldLoadReferenceData) {
            setShouldLoadReferenceData(false);
            getCommonReferenceDataReduxRequest(dispatch)(
                DASHBOARD_REFERENCE_DATA_TYPES,
                ReferenceDataSourceTypes.SERVICE_CONFIGURATION,
                CommonReferenceDataPageTypes.DASHBOARD
            );
        }
    }, [referenceData.isLoading.dashboard, shouldLoadReferenceData]);

    const isReferenceDataPresent = useMemo(() => {
        return !!(
            referenceData.axnCompanies &&
            referenceData.rpServiceTypes
        );
    }, [
        referenceData.axnCompanies,
        referenceData.rpServiceTypes
    ]);

    const isAdmin = useMemo(() => {
        return isAxnAdminUser(session.user);
    }, [session.user]);

    const userCompany = useMemo(() => {
        if (session.user && isReferenceDataPresent) {
            const company = referenceData.axnCompanies.find(company => company.id === session.user.organizationID);
            return company || null;
        }
        return null;
    }, [isReferenceDataPresent]);

    useEffect(() => {
        if (isReferenceDataPresent && !searchResults.isLoading && shouldLoadDashboardData) {
            const companyId = isAdmin ? searchFilters.companyId : session.user.organizationID;
            setShouldLoadDashboardData(false);
            handleSearch({
                companyId,
                startDate: searchFilters.startDate,
                endDate: searchFilters.endDate,
                timeRangeType: searchFilters.timeRangeType
            }, true);
        }
    }, [isReferenceDataPresent, searchResults.isLoading, shouldLoadDashboardData, isAdmin]);

    const isLoading = useMemo(() => {
        return referenceData.isLoading.dashboard;
    }, [referenceData.isLoading.dashboard]);

    const isLoaded = useMemo(() => {
        return !isLoading && isReferenceDataPresent && !shouldLoadReferenceData;
    }, [isLoading, isReferenceDataPresent, shouldLoadReferenceData]);

    const getGroupByForStartAndEndDate = (startDate: Date, endDate: Date) => {
        const duration = differenceInDays(endDate, startDate);
        if (duration < 2) {
            return DashboardGroupByTypes.DATE_YEAR_MONTH_DAY_HOUR;
        } else {
            return DashboardGroupByTypes.DATE_YEAR_MONTH_DAY;
        }
    };

    const getGroupByForTimeRange = (timeRangeType: TimeRangeTypes) => {
        switch (timeRangeType) {
            case TimeRangeTypes.PAST_TWENTY_FOUR_HOURS:
                return DashboardGroupByTypes.DATE_YEAR_MONTH_DAY_HOUR;
            default:
                return DashboardGroupByTypes.DATE_YEAR_MONTH_DAY;
        }
    };

    const handleSearch = (newSearchFilters: DashboardSearchFiltersState, forceSearch?: boolean) => {
        const { companyId, startDate, endDate } = newSearchFilters;

        if (
            startDate !== searchFilters.startDate ||
            endDate !== searchFilters.endDate ||
            companyId !== searchFilters.companyId ||
            forceSearch
        ) {
            const groupBy = newSearchFilters.timeRangeType === TimeRangeTypes.CUSTOM
                ? getGroupByForStartAndEndDate(newSearchFilters.startDate, newSearchFilters.endDate)
                : getGroupByForTimeRange(newSearchFilters.timeRangeType);
            dispatch(setDashboardSearchFilters(newSearchFilters));
            getDashboardDataReduxRequest(dispatch)(
                companyId,
                startDate,
                endDate,
                groupBy,
                referenceData.rpServiceTypes
            );
        }
    };

    const transactionsTableHeaderButton = useMemo(() => {
        if (session.userPermissions.canViewTransactionReport) {
            const onClickViewAll = () => {
                const queryParametersObject = {
                    companyIds: ""
                };
                if (searchFilters.companyId) {
                    queryParametersObject.companyIds = searchFilters.companyId;
                } else {
                    delete queryParametersObject.companyIds;
                }

                const queryParameters = createSearchParams(queryParametersObject).toString();
                navigateToUtilWithQueryParameters(REPORTS_TRANSACTIONS_URL, navigate, routerLocation.pathname, queryParameters);
            };

            return (
                <Button id="dashboard-transaction-table-view-all-transactions" onClick={onClickViewAll}>
                    {resources.common.buttons.viewAll.label}
                </Button>
            );
        }

        return null;
    }, []);

    const servicesTableHeaderButton = useMemo(() => {
        if (session.userPermissions.canListRPServices) {
            const onClickViewAll = () => {
                navigateToUtil(VERIFICATION_SERVICES_URL, navigate, routerLocation.pathname);
            };

            return (
                <Button id="dashboard-services-table-view-all-services" onClick={onClickViewAll}>
                    {resources.common.buttons.viewAll.label}
                </Button>
            );
        }

        return null;
    }, []);

    return (
        <CardContainer>
            <ContentPageBody isMaxWidth={true}>
                {isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
                {isLoaded && (
                    <DashboardSearchForm
                        requestInProgress={searchResults.isLoading}
                        isAdmin={isAdmin}
                        userCompany={userCompany}
                        companies={referenceData.axnCompanies}
                        searchFilters={searchFilters}
                        handleSearch={handleSearch}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.summary && (
                    <DashboardSummaryContainer dashboardSummary={searchResults.summary} />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.verificationCounts && (
                    <DashboardVerificationCountsContainer
                        timeRangeType={searchFilters.timeRangeType}
                        startDate={searchFilters.startDate}
                        endDate={searchFilters.endDate}
                        dashboardVerificationCounts={searchResults.verificationCounts}
                    />
                )}
                {isLoaded && !searchResults.isLoading && searchResults.verificationTransactions && (
                    <CardContainer>
                        <ContentPageHeader title={resources.tables.dashboardVerificationTransactions.title} headerButton={transactionsTableHeaderButton} />
                        <ContentPageBody isMaxWidth={true}>
                            <DashboardVerificationTransactionsTableContainer
                                canViewTransactionReport={session.userPermissions.canViewTransactionReport}
                                verificationTransactions={searchResults.verificationTransactions}
                            />
                        </ContentPageBody>
                    </CardContainer>
                )}
                {isLoaded && !searchResults.isLoading && searchResults.verificationTransactions && (
                    <CardContainer>
                        <ContentPageHeader title={resources.tables.dashboardVerificationServices.title} headerButton={servicesTableHeaderButton} />
                        <ContentPageBody isMaxWidth={true}>
                            <DashboardVerificationServicesTableContainer
                                canListRPServices={session.userPermissions.canListRPServices}
                                verificationServices={searchResults.verificationServices}
                            />
                        </ContentPageBody>
                    </CardContainer>
                )}
                {isLoaded && searchResults.isLoading && (
                    <LoadingIndicator title={resources.common.loadingIndicatorText} />
                )}
            </ContentPageBody>
        </CardContainer>
    );
};